import Vue from 'vue'
import _ from 'lodash'
import Vuex from 'vuex'
import {
  GET_SEARCH_USER,
  RESET_SEARCH_USER,
  UPDATE_PATIENT,
  UPDATE_SEARCH_USER,
  DOWNLOAD_DOCUMENT,
  GET_DOCUMENT_URL,
  GET_DOCUMENT_URLS,
  MERGE_FILE_OBJECTS,
  SET_FILE_OBJECT_URL,
  CREATE_FILE_UPLOAD,
  GET_BIOMARKERS,
  GET_BIOMARKER_METHODS,
  GET_BIOMARKER_METHOD_RESULTS,
  SET_BIOMARKER_TEST_RESULTS,
  CLEAR_BIOMARKER_TEST_RESULTS,
  SET_BIOMARKER_DATA
} from '@/store/mutation-types'
import api from '@/services/viper-service'
import { patientService } from '@/services/patient-service'
import { biomarkerService } from '@/services/biomarker-service'
import { normalize, denormalize } from 'normalizr'
import { schemas } from '@/store/schemas'
import * as anatomicSiteModule from '@/store/modules/anatomic-site'
import * as layoutModule from '@/store/modules/layout'
import * as organizationModule from '@/store/modules/organization'
import * as appVersionModule from '@/store/modules/app-version'
import * as uploadModule from '@/store/modules/upload'
import * as documenTypeModule from '@/store/modules/document-type'
import * as trialIdentificationNotificationModule from '@/store/modules/trial-identification-notification'
import * as filterModule from '@/store/modules/filter'
import * as membershipModule from '@/store/modules/membership'
import * as snackbar from '@/store/modules/snackbar'
import axios from 'axios'
import uuid from 'uuid'
import { MyActiveOrganization, MyUser } from '@/helpers/my-account-helper'
// import files not as modules
import * as trials from '@/store/modules/trial'
import * as trialIdentifications from '@/store/modules/trial-identification'
import * as patientDiagnoses from '@/store/modules/patient-diagnosis'
import * as diagnosis from '@/store/modules/diagnosis'
import * as demographics from '@/store/modules/demographics'
import * as patientDemographics from '@/store/modules/patient-demographics'
import * as trialNotificationPreferences from '@/store/modules/trial-notification-preferences'
import * as reminders from '@/store/modules/reminders'
import * as patients from '@/store/modules/patient'
import * as patientView from '@/store/modules/patient-view'
import * as event from '@/store/modules/user-notification'

export const mutations = {
  ...trials.mutations,
  ...trialIdentifications.mutations,
  ...patientDiagnoses.mutations,
  ...diagnosis.mutations,
  ...demographics.mutations,
  ...patientDemographics.mutations,
  ...snackbar.mutations,
  ...trialNotificationPreferences.mutations,
  ...reminders.mutations,
  ...patients.mutations,
  ...patientView.mutations,
  ...event.mutations,

  [SET_FILE_OBJECT_URL](state, { fileObjectId, url }) {
    Vue.set(state.fileObjectDownloadUrls, fileObjectId, url)
  },
  [UPDATE_SEARCH_USER](state, data) {
    state.searchUser = data[0]
  },
  [MERGE_FILE_OBJECTS](state, fileObjects) {
    state.fileObjects = { ...state.fileObjects, ...fileObjects }
  },
  [SET_BIOMARKER_TEST_RESULTS](state, normalizedResults) {
    state.biomarkerTestResults = normalizedResults
  },
  [SET_BIOMARKER_DATA](state, normalizedBiomarkerData) {
    state.biomarkers = { ...state.biomarkers, ...normalizedBiomarkerData.entities.biomarkers }
    state.biomarkerMethods = {
      ...state.biomarkerMethods,
      ...normalizedBiomarkerData.entities.biomarkerMethods
    }
    state.biomarkerMethodResults = {
      ...state.biomarkerMethodResults,
      ...normalizedBiomarkerData.entities.biomarkerMethodResults
    }
  },
  ['SET_GENOMICS_REPORTS'](state, normalizedGenomicsReportData) {
    state.genomicsReports = normalizedGenomicsReportData.entities.genomicsReports
    state.cases = { ...state.cases, ...normalizedGenomicsReportData.entities.cases }
  },
  ['REFRESH_PATIENT_SEARCH'](state, search) {
    state.refreshPatientSearch = search
  },
  ['REFRESH_CASE_SEARCH'](state, search) {
    state.refreshCaseSearch = search
  },
  ['REFRESH_DOCUMENTS'](state, uuid) {
    state.refreshDocuments = uuid
  }
}

export const actions = {
  ...trials.actions,
  ...trialIdentifications.actions,
  ...patientDiagnoses.actions,
  ...diagnosis.actions,
  ...demographics.actions,
  ...patientDemographics.actions,
  ...snackbar.actions,
  ...trialNotificationPreferences.actions,
  ...reminders.actions,
  ...patients.actions,
  ...patientView.actions,
  ...event.actions,

  // PATIENTS
  [UPDATE_PATIENT](context, patient) {
    return new Promise(res => {
      patientService.update(MyActiveOrganization.id, patient).then(response => {
        res(response.data)
      })
    })
  },
  [GET_SEARCH_USER](context, params) {
    api.users.search(params.email).then(response => {
      context.commit(UPDATE_SEARCH_USER, response.data)
    })
  },
  [RESET_SEARCH_USER](context) {
    context.commit(UPDATE_SEARCH_USER, [])
  },
  async [GET_BIOMARKERS]({ commit }, methodId) {
    const response = await biomarkerService.getBiomarkers({ methodId: methodId })
    let normalized = normalize(response.data, schemas.biomarkerList)
    commit(SET_BIOMARKER_DATA, normalized)
    return response.data
  },
  async [GET_BIOMARKER_METHODS]({ commit }, biomarkerId) {
    const response = await biomarkerService.getBiomarkerMethods({ biomarkerId: biomarkerId })
    let normalized = normalize(response.data, schemas.biomarkerMethodList)
    commit(SET_BIOMARKER_DATA, normalized)
    return response.data
  },
  async [GET_BIOMARKER_METHOD_RESULTS]({ commit }, { biomarkerId, methodId }) {
    const response = await biomarkerService.getBiomarkerMethodResults({
      biomarkerId: biomarkerId,
      methodId: methodId
    })
    let normalized = normalize(response.data, schemas.biomarkerMethodResultList)
    commit(SET_BIOMARKER_DATA, normalized)
    return response.data
  },
  [CLEAR_BIOMARKER_TEST_RESULTS]({ commit }) {
    commit(SET_BIOMARKER_TEST_RESULTS, {})
  },
  ['REFRESH_PATIENT_SEARCH']({ commit }, refreshValue) {
    commit('REFRESH_PATIENT_SEARCH', refreshValue)
  },
  ['REFRESH_CASE_SEARCH']({ commit }, refreshValue) {
    commit('REFRESH_CASE_SEARCH', refreshValue)
  }
}

export const getters = {
  ...trials.getters,
  ...trialIdentifications.getters,
  ...patientDiagnoses.getters,
  ...demographics.getters,
  ...patientDemographics.getters,
  ...trialNotificationPreferences.getters,
  ...patients.getters,
  ...patientView.getters,
  ...event.getters,

  /**
   * Get the number of notes on a case
   */
  getNormalizedState: state => () => {
    return {
      ...state,
      slides: state.slide.slides,
      stainTypes: state.stainType.stainTypes,
      anatomicSites: state.anatomicSite.anatomicSites,
      caseForms: state.caseForm.caseForms,
      caseSlides: state.caseSlide.caseSlides,
      documents: state.documents,
      documentTypes: state.documentTypes,
      fileUploads: state.upload.fileUploads,
      fileObjects: state.fileObjects,
      forms: state.form.forms,
      organizations: state.organization.organizations,
      labels: state.slide.labels,
      memberships: state.membership.memberships,
      roles: state.membership.roles,
      tags: state.tag.tags
    }
  }
}

export const state = {
  ...snackbar.state,
  addSlidesDialogVisible: false,
  searchUser: null,
  users: {},
  fetchingCases: false,
  fetchingAssignments: false,
  cases: {},
  caseFilters: {},
  refreshCases: null,
  patients: {},
  hasCases: false,
  caseAssignments: {},
  assignmentPage: {},
  fieldOptions: {},
  fieldOptionAliases: {},
  slideViewers: [],
  slideData: {},
  hasSlideData: false,
  formResponses: {},
  fileObjects: {},
  fileObjectDownloadUrls: {},
  activeAIAnnotations: [],
  documents: {},
  documentTypes: {},
  refreshDocuments: null,
  biomarkers: {},
  biomarkerMethods: {},
  biomarkerMethodResults: {},
  biomarkerTestResults: {},
  breadcrumb: {
    pathCase: null,
    form: null,
    patient: null,
    slide: null,
    trial: null
  },

  // Pulling up state from trials files as part of
  // keeping MEGA STORE with different files
  trials: {},
  organizationTrials: {},
  fetchingTrials: false,
  trialIdentifications: {},
  refreshTrialIdentifications: null,
  refreshReminders: null,
  trialEligibilities: {},
  patientEligibilityNotes: {},
  patientEligibilityVerifications: {},
  inclusions: {},
  exclusions: {},
  patientEligibilityEvaluationResults: {},
  patientDiagnoses: {},
  diagnoses: {},
  diagnosisHistologies: {},
  diagnosisSites: {},
  diagnosisGrades: {},
  diagnosisStages: {},
  metastases: {},
  races: {},
  ethnicities: {},
  genders: {},
  patientDemographics: {},
  trialNotificationPreferences: {},
  presentations: {},
  refreshPresentations: null,
  refreshPresentationContent: null,
  refreshPresentationPatient: null,
  presentationCaseSlides: {},
  presentationTrials: {},
  presentationPreparers: {},
  presentationTags: {},
  presentationCaseCounts: {},
  selectedPresentationContent: null,
  selectedPresentationId: null,
  fetchingSelectedPatient: false,
  selectedPatient: null,
  refreshPatientSearch: null,
  refreshCaseSearch: null,
  refreshPatientCases: null,
  refreshPatientTrials: null,
  refreshTrials: null,
  refreshUserNotifications: null,
  unreadCounts: null
}

export const modules = {
  anatomicSite: anatomicSiteModule,
  layout: layoutModule,
  organization: organizationModule,
  appVersion: appVersionModule,
  upload: uploadModule,
  trialNotification: trialIdentificationNotificationModule,
  documentType: documenTypeModule,
  filter: filterModule,
  membership: membershipModule
}

export function ClinicalStore() {
  return new Vuex.Store({
    modules,
    mutations,
    actions,
    getters,
    state
  })
}
